import { Modal } from './Modal'
import { UIButton } from './UIButton'
import type { ReactNode } from 'react'

export interface ConfirmDialogOptions {
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Style the confirm button as destructive. */
  tone?: 'primary' | 'danger'
}

export interface ConfirmDialogProps extends ConfirmDialogOptions {
  open: boolean
  isConfirming?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Controlled confirmation dialog (KAN-1179) built on Modal. Prefer the promise-based
 * `useConfirm()` hook (DialogProvider) — this component is the presentational layer.
 * Destructive confirms disable overlay-click-to-dismiss to avoid accidental data loss.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  isConfirming = false,
  onConfirm,
  onCancel
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      hideCloseButton
      closeOnOverlayClick={tone !== 'danger'}
      ariaLabel={title}
      footer={
        <>
          <UIButton variant="secondary" onClick={onCancel} disabled={isConfirming}>
            {cancelLabel}
          </UIButton>
          <UIButton
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            isLoading={isConfirming}
          >
            {confirmLabel}
          </UIButton>
        </>
      }
    >
      {message}
    </Modal>
  )
}

export default ConfirmDialog
